import { Link, type LinkProps } from '@chakra-ui/react/link';
import { type ComponentPropsWithoutRef, forwardRef, type KeyboardEvent, type MouseEvent } from 'react';

import { type BitkitIconComponent } from '../../icons';

export interface BitkitLinkProps extends LinkProps {
  isExternal?: boolean;
  suffixIcon?: BitkitIconComponent;
  suffixIconSize?: ComponentPropsWithoutRef<BitkitIconComponent>['size'];
  state?: 'disabled';
}

const BitkitLink = forwardRef<HTMLAnchorElement, BitkitLinkProps>((props, ref) => {
  const {
    children,
    href,
    isExternal,
    onClick,
    onKeyDown,
    rel,
    suffixIcon: SuffixIcon,
    suffixIconSize,
    state,
    target,
    ...rest
  } = props;
  const isDisabled = state === 'disabled';
  const effectiveTarget = isExternal ? '_blank' : target;
  const effectiveRel = isExternal ? (rel ? `${rel} noreferrer noopener` : 'noreferrer noopener') : rel;
  const handleClick = isDisabled
    ? (e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        e.stopPropagation();
      }
    : onClick;
  const handleKeyDown = isDisabled
    ? (e: KeyboardEvent<HTMLAnchorElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    : onKeyDown;

  return (
    <Link
      aria-disabled={isDisabled || undefined}
      href={isDisabled ? undefined : href}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      ref={ref}
      rel={effectiveRel}
      target={effectiveTarget}
      {...rest}
    >
      {children}
      {SuffixIcon &&
        (suffixIconSize ? <SuffixIcon size={suffixIconSize} /> : <SuffixIcon css={{ height: '1lh', width: '1lh' }} />)}
    </Link>
  );
});

BitkitLink.displayName = 'BitkitLink';

export default BitkitLink;
